import { useState } from "react";
import { Capacitor } from "@capacitor/core";

// BlindsPro CRM Help Center
// Shown inside the app only, so only signed in users with an active trial
// or paid plan can open it (expired trials see the paywall instead).
// PLAY POLICY: topics marked webOnly (plans, prices) are hidden on Android.

var TOPICS = [
  {
    id: "start",
    icon: "home",
    title: "Getting Started",
    sections: [
      {
        h: "The home screen",
        p: ["The home screen has 12 tiles. Tap a tile to open it, tap it again to close it."],
        list: [
          "Search: find any customer.",
          "New Customer: add a customer.",
          "Recent Calls: customers you contacted most recently.",
          "Upcoming: your next jobs and customer reminders.",
          "Reminders: reminders that are not about a customer.",
          "WA Blast: send one WhatsApp message to many customers.",
          "Team Chat: message your team.",
          "New Quote: create a quote.",
          "Measurements: measure windows and work out square feet.",
          "Route Today: today's jobs in order with directions.",
          "WA Templates: save messages you send often.",
          "Contacts: import and export customers.",
        ],
      },
      {
        h: "Status counters",
        p: ["Below the tiles you will see status counters with a coloured dot, such as Lead 12 or Quoted 5. Tap one to see only those customers (it turns teal). Tap Clear to see everyone again."],
      },
      {
        h: "Bottom tabs",
        list: [
          "Home: the main screen.",
          "Dashboard: your numbers for this month.",
          "Calendar: jobs by day.",
          "Accounting: payments collected and outstanding.",
          "Settings: logo, team, account and Help.",
        ],
      },
      {
        h: "Dark mode",
        p: ["Tap the moon or sun button at the top right of any screen, or use the Dark mode switch in Settings, to switch between dark and light mode."],
      },
    ],
  },
  {
    id: "signin",
    icon: "key",
    title: "Signing In and Passwords",
    sections: [
      {
        h: "Sign in",
        p: ["Open the app, tap Sign In, and enter your email and password. Tap 👁️ to see the password as you type."],
      },
      {
        h: "Forgot your password",
        steps: [
          "On the first screen, or the Sign In screen, tap Forgot Password?",
          "Enter your email and tap Send Reset Email.",
          "Open the email and follow the link to choose a new password.",
          "Sign in with the new password.",
        ],
        p: ["This is also how you change your password at any time."],
      },
      {
        h: "Passwords must have",
        list: [
          "At least 8 characters.",
          "An uppercase letter and a lowercase letter.",
          "A number.",
          "A special character, for example ! @ # or $.",
        ],
      },
      {
        h: "Signed out on another device",
        p: ["Your account can be signed in on one device at a time. If you sign in somewhere else, the first device is signed out and shows a message saying so. Just sign in again on the device you want to use."],
      },
      {
        h: "Adding staff (owners)",
        p: ["Staff do not create their own account. Invite them from Settings, Team. They receive an email to set a password, then sign in with that email. See Team and Team Chat for details."],
      },
      {
        h: "New account (for reference)",
        steps: [
          "Tap Create New Account and enter your name, email, phone (optional), company name and a password.",
          "Tap Continue, then Start Free Trial. No credit card is needed. If you were given a license key, enter it instead.",
          "Tap Sign In Now and sign in with the email and password you just created.",
        ],
      },
    ],
  },
  {
    id: "customers",
    icon: "users",
    title: "Finding and Adding Customers",
    sections: [
      {
        h: "Add a customer",
        steps: [
          "Tap New Customer on the home screen.",
          "Fill in name, phone, email, address, city and product interest. Only the details you have are needed. As you type the city, cities you already use are suggested.", // PATCH oct21-ui-lists
          "Choose New lead or Existing.",
          "Add any notes, then tap Save customer.",
        ],
      },
      {
        h: "Search",
        p: [
          "Tap Search, then type any part of a name, phone number, city or product, for example smith, surrey or 5551. Upper or lower case does not matter, and results update as you type.",
          "Phone numbers work in any format, with or without dashes, spaces, brackets or the leading 1. Part of a number works too, for example the last 4 digits.",
          "If you search for a phone number and nobody matches, tap + Add as new customer and the number is filled in for you. Tap the x to clear the search.",
        ],
      },
      {
        h: "Filters and sorting",
        list: [
          "All Statuses: show only one stage, for example Quoted.",
          "All Cities: show only customers in one city.",
          "Recent: customers you contacted most recently at the top. This is the default.",
          "A to Z: sort by name.",
          "Newest: newest customers at the top.",
        ],
      },
      {
        h: "Quick actions on the list",
        p: ["Each customer card has a Quick note box, plus Call and WhatsApp buttons, so you can act without opening the profile. Tap the card itself to open the full profile."],
      },
    ],
  },
  {
    id: "profile",
    icon: "user",
    title: "Customer Profile",
    sections: [
      {
        p: ["Tap any customer to open their profile. Everything about that customer lives here."],
      },
      {
        h: "Top buttons",
        list: [
          "Call: dials the customer from your phone and records today as the last contact.",
          "WhatsApp: opens a WhatsApp chat with the customer and records the contact.",
          "Directions: opens the address in Google Maps.",
          "Under the buttons, tap the address for directions, or the email address to write to the customer.",
        ],
      },
      {
        h: "Quotes and measurements",
        list: [
          "Create quote: builds a new quote for this customer.",
          "Quote history: shows past quotes with date and total. Tap View full quote to see details, the WhatsApp icon to send it again, the pencil to edit it or the trash can to delete it.",
          "Measurements: shows saved measurements with total sq ft, number of windows and price. Tap the pencil to change them or the trash can to remove them.",
          "Save to phone contacts: saves the customer to your phone's contacts.",
        ],
      },
      {
        h: "Send WhatsApp Template",
        p: ["Pick one of your saved messages and tap the WhatsApp icon next to it. The customer's name is filled in automatically where the template says {name}."],
      },
      {
        h: "Schedule job and Add reminder",
        list: [
          "Schedule job: books a job with date, time, price and notes. Under Also remind me you can add alerts 1 hour, 12 hours or 1 day before, or a custom time.",
          "Add reminder: sets a one time reminder for this customer with a short note. Your phone will alert you at that time.",
        ],
      },
      {
        h: "Follow up",
        p: [
          "Tap what you just did: Called, Texted, Sent Quote, Installation Done, No Answer or Left Voicemail. Each tap updates the last contacted date and is saved in History.",
          "Sent Quote also moves the status to Quoted, and Installation Done moves it to Done.",
        ],
      },
      {
        h: "Scheduled Jobs",
        p: ["Lists this customer's jobs with price and payment status. Jobs without a price show a grey No price tag. Tap the pencil to change the date, time or price, or to delete the job."],
      },
      {
        h: "Job Status",
        p: ["Tap a stage to move the customer along: Lead, Consulted, Quoted, Ordered, Installing, Done. The selected stage is teal. The status shows on the profile and in the home screen counters."],
      },
      {
        h: "Details",
        p: ["Shows phone, email, address, city and product interest. Tap Edit to change them, or to mark the customer as a New Lead or Existing Customer. A Save changes bar appears at the bottom once you change something."],
      },
      {
        h: "Notes and Photos",
        p: ["Type a note and tap Add. Each note is saved with the date and time. Tap Upload Photo to attach pictures of windows or finished jobs. Tap View to open a photo or Delete photo to remove it. Use the pencil or trash can next to a note to edit or delete it."],
      },
      {
        h: "Delete Customer",
        p: ["Permanently removes the customer with their jobs, reminders and notes. This cannot be undone."],
      },
    ],
  },
  {
    id: "referrals",
    icon: "link",
    title: "Referrals (Referred By)",
    sections: [
      {
        h: "What it is for",
        p: ["Word of mouth is the best source of blinds jobs. Referred By lets you record which customer sent you each new customer, so you know who your best supporters are and can thank or reward them."],
      },
      {
        h: "Before you start",
        p: ["The person who referred the new customer must already be saved as a customer in the app. If they are not, add them first with New Customer."],
      },
      {
        h: "How to link a referral",
        steps: [
          "Open the new customer's profile.",
          "Scroll down to Details and find the Referred by box.",
          "Type part of the referrer's name or phone number.",
          "A list of matching customers appears. Tap the right person.",
          "You will see Referral linked! and the referrer's name now shows under Referred by with a link icon.",
        ],
      },
      {
        h: "Viewing and changing it",
        list: [
          "Tap the referrer's name to jump straight to their profile.",
          "Tap Remove to unlink them, for example if you picked the wrong person. You can then link the correct one.",
          "Each customer can have one referrer. One customer can refer as many people as they like.",
        ],
      },
      {
        h: "Who a customer has referred",
        p: ["Open the referrer's profile. Under Details you will see Referred X customers with their names. Tap a name to open that customer."],
      },
      {
        h: "Tips",
        list: [
          "Link the referral right after saving a new customer, while you remember who sent them.",
          "Tap the referrer's name to quickly call or WhatsApp them a thank you.",
          "A customer cannot be linked as their own referrer.",
        ],
      },
    ],
  },
  {
    id: "jobs",
    icon: "calendar",
    title: "Jobs and Upcoming",
    sections: [
      {
        h: "Book a job",
        p: ["Open the customer's profile and tap Schedule job. Enter date, time, price and notes, choose any reminders, then tap Schedule job at the bottom of the form."],
      },
      {
        h: "The Upcoming tile",
        p: ["Shows your next jobs and customer reminders together, in time order. The number on the tile tells you how many there are."],
        list: [
          "Tap a job or the pencil to edit its date, time, price or notes. The trash can in the edit box deletes it.", // PATCH oct21-ui-more
          "The bell adds reminders before that job.",
          "The green tick marks the job as done and removes it from the list.",
        ],
      },
      {
        h: "Jobs tomorrow",
        p: ["If you have jobs tomorrow, a Jobs tomorrow box appears on the home screen. Tap the WhatsApp icon next to a customer to send them a ready made reminder about their appointment. Tap the x to hide the box."],
      },
    ],
  },
  {
    id: "reminders",
    icon: "clock",
    title: "Reminders",
    sections: [
      {
        h: "Customer reminders",
        p: ["Open the customer's profile, tap Add reminder, choose a date and time, add a note and tap Set reminder. You can also add reminders when you schedule a job."],
      },
      {
        h: "Personal reminders",
        p: ["For anything that is not about a customer, for example ordering stock. Tap Reminders on the home screen, then + Add reminder. Tap a reminder or the pencil to edit it, or the green tick to dismiss it."],
      },
      {
        h: "When a reminder is due",
        list: [
          "Your phone shows a notification, even when the app is closed.",
          "A Reminder due card appears at the top of the home screen. Tap Done when you have dealt with it.",
        ],
      },
      {
        h: "Make sure reminders arrive on time (Android)",
        p: ["When the app asks, allow notifications and turn on Alarms and reminders for BlindsPro CRM. See Android Tips for battery settings."],
      },
    ],
  },
  {
    id: "quotes",
    icon: "file",
    title: "Quotes",
    sections: [
      {
        h: "Start a quote",
        p: ["Tap Create Quote on a customer's profile, and their name and phone are filled in for you. Or tap New Quote on the home screen and search for the customer by name or phone. You can also type a name and phone by hand."],
      },
      {
        h: "Add items",
        steps: [
          "Choose a tab: Blinds, Screens or Automation. You can use more than one tab in the same quote.",
          "Pick the type from the list, for example Zebra Blind, Retractable Screen Door or Rechargeable Motor.",
          "Enter the quantity.",
          "Enter the total price for that line (optional). This is the price for all units on the line, not for each unit.",
          "Each line has a TAX chip. Teal TAX means tax applies. Tap it to switch that line to No tax. Charges have the same chip.",
          "Tap + Add item at the bottom for more lines, or the x at the end of a line to remove it.", // PATCH oct21-ui-quote
        ],
      },
      {
        h: "Charges",
        p: ["Below the items, add extra charges by entering an amount. Leave a charge blank to skip it."],
        list: [
          "Blinds tab: Installation, Removal and Disposal, plus one charge you can name yourself.",
          "Screens tab: Installation, plus one charge you can name yourself.",
          "Automation tab: one charge you can name yourself.",
        ],
      },
      {
        h: "Notes, totals and preview",
        p: [
          "Add notes such as includes installation or free measurement. The Summary shows each section, the subtotal, your tax lines (set in Settings, Tax and Currency) and the total. Lines without a price show as TBD.", // PATCH oct21-ui-quote
          "The WhatsApp Preview shows exactly what the customer will receive.",
        ],
      },
      {
        h: "Save and send",
        steps: [
          "Tap Save Quote (Update Quote when you edit a saved quote). You must save before sending.", // PATCH oct21-ui-quote
          "Tap WhatsApp or Email, side by side under it. Email opens your email app with the quote filled in. Tap Back at the top to leave.",
        ],
        p: ["Saved quotes appear in the customer's Quote History. A quote is only added to a customer's history if you opened it from their profile or picked them in the search, not if you typed the name by hand."],
      },
    ],
  },
  {
    id: "measure",
    icon: "ruler",
    title: "Measurements",
    sections: [
      {
        h: "Measure a job",
        steps: [
          "Tap Measurements on the home screen.",
          "Under Customer, search by name or phone and tap the customer (optional). Under Job or room, add a description, for example Main floor.",
          "Choose the Rounding (see below).",
          "For each window, type the width and height in whole inches, then pick any fraction (1/8 to 7/8) from the small list next to it. The cursor moves to the next box for you.",
          "Square feet appear for each window, with totals at the bottom. Tap the x on a row to clear it.",
          "The screen starts with 10 rows. Tap + Add window for another row. Pressing Enter on the last box, or a laser shot on the last row, adds a row for you.",
          "Enter your price per sq ft (optional) to see the Total price.",
          "Tap Save measurements.",
        ],
      },
      {
        h: "Rounding",
        list: [
          "Nearest 6 in: each size is rounded up to the next 6 inches. Square feet are rounded up to the next 0.5.",
          "Nearest 1 ft: each size is rounded up to the next 12 inches. Square feet are rounded up to the next whole number.",
          "Every window counts as at least the minimum sq ft your company sets in Settings, Measurements (9 unless changed, 0 means no minimum). These windows show a grey min tag.",
          "The total sq ft is rounded up to a whole number.",
        ],
        p: ["Square feet = rounded width times rounded height, divided by 144."],
      },
      {
        h: "Tips for typing sizes",
        tips: true,
        p: ["You can also tap the i button next to Window sizes to see these tips on the Measurements screen."],
      },
      {
        h: "Share and export",
        list: [
          "Export Excel: a formatted spreadsheet with every window, totals and price. Save first, then export. On Android, choose where to save or share it.",
          "WhatsApp: sends the list of sizes and totals as a message.",
          "Only rows with a width and height are saved, exported or sent.",
        ],
      },
      {
        h: "Edit a saved measurement",
        p: ["Open the customer's profile, tap Measurements, then the pencil next to the record. Make your changes and tap Update measurements."],
      },
    ],
  },
  {
    id: "laser",
    icon: "bluetooth",
    title: "Laser Measure (Bluetooth)",
    sections: [
      {
        h: "Supported lasers",
        list: [
          "Bosch GLM CG series lasers with Bluetooth. Tested.",
          "Leica DISTO lasers with Bluetooth (beta). These should work but have not been tested on every model yet.",
        ],
        p: ["Laser measure is for the Android app. You can always type sizes by hand."],
      },
      {
        h: "Connect your laser",
        steps: [
          "Turn on the laser and switch on its Bluetooth.",
          "Open Measurements on the home screen.",
          "Under Laser, tap Connect laser. If your phone asks, allow Bluetooth access.",
          "The app finds the closest laser and connects. You will see a message saying it is connected, and a teal dot.",
          "Tap Disconnect laser when you are done. Leaving Measurements also disconnects it.",
        ],
      },
      {
        h: "Measuring",
        list: [
          "Each shot fills the next box: width first, then height, then the next row.",
          "The line next to the button shows which box is next, for example Next: row 3 width.",
          "To measure a different box, tap it first. The next shot goes there.",
          "Sizes are rounded down to the nearest 1/8 inch.",
          "You can still type or correct any size by hand.",
        ],
      },
      {
        h: "Laser modes",
        list: [
          "Single: one shot fills the box straight away. Best for most windows.",
          "Average: takes 3 shots and uses the average.",
          "Smallest: takes 3 shots and uses the smallest. This is the standard for inside mount blinds.",
          "Dots show how many shots have been taken. Tap the x to start that box again.",
        ],
      },
      {
        h: "If it will not connect",
        list: [
          "Make sure the laser's Bluetooth is on and the laser is close to your phone.",
          "Set the laser to single distance measurement.",
          "Close the laser maker's own app if it is open, since a laser connects to one app at a time.",
          "Turn the laser off and on, then tap Connect laser again.",
        ],
      },
      {
        h: "Which plans include it",
        webOnly: true,
        p: ["Laser measure is included in the Pro and Business plans and in the free trial. It is not part of the Starter plan."],
      },
    ],
  },
  {
    id: "whatsapp",
    icon: "whatsapp",
    title: "WhatsApp Tools",
    sections: [
      {
        h: "WA Templates",
        p: [
          "Save messages you send often. Tap WA Templates, then + New template. Enter a name and the message, and tap Save template.",
          "Type {name} where the customer's name should go, for example Hi {name}, thanks for your interest. Tap a template or its pencil to edit it, or the trash can to delete it.",
        ],
      },
      {
        h: "Send a template",
        p: ["Open a customer's profile, tap Send WhatsApp template, then tap the WhatsApp icon next to the message you want."],
      },
      {
        h: "WA Blast (one message to many customers)",
        steps: [
          "Tap WA Blast on the home screen.",
          "Filter by status or city, or search by name, then tick the customers you want. Select All ticks everyone in the list.",
          "Pick a template or type your message.",
          "Tap Copy numbers, open WhatsApp, start a New Broadcast and paste the numbers.",
          "Go back to the app, tap Copy message, paste it into WhatsApp and send.",
        ],
        p: ["Each customer receives it as a personal message. WhatsApp only delivers broadcast messages to people who have saved your number in their phone."],
      },
    ],
  },
  {
    id: "route",
    icon: "map",
    title: "Route Today",
    sections: [
      {
        p: ["Tap Route Today to see today's jobs in time order with each customer's address."],
        list: [
          "Open in Maps: directions to one job.",
          "Call and WhatsApp: contact the customer.",
          "Full Route: opens Google Maps with every stop in order, starting from where you are.",
          "Google Maps allows 10 stops per route, so busy days are split into parts. Open each part in order.",
          "Share to Staff: sends today's job list and route links on WhatsApp.",
        ],
      },
    ],
  },
  {
    id: "numbers",
    icon: "chart",
    title: "Dashboard, Calendar and Accounting",
    sections: [
      {
        h: "Dashboard",
        p: ["Shows total customers, new leads this month, jobs this month and revenue this month (job prices scheduled this month). The Pipeline shows how many customers are at each stage, as a share of all customers with a job status. Next jobs lists what is coming up."], // PATCH oct21-ui-dash
      },
      {
        h: "Calendar",
        p: ["A month view of your jobs. Use the arrows to change month. Today is highlighted, and every job for the month is listed below the calendar in date order. Jobs without a price show a grey No price tag."], // PATCH oct21-ui-dash
      },
      {
        h: "Accounting",
        list: [
          "Collected: money received, including deposits.",
          "Outstanding: money still owed.",
          "Filter jobs by All, Unpaid, Deposit or Paid. Jobs without a price show a grey No price tag and are not counted as unpaid.", // PATCH oct21-ui-dash
          "Tap Update Payment to set the status, payment method (Cash, Cheque, E Transfer or Card), deposit amount and payment date.",
          "Tap the customer's name to open their profile.",
        ],
      },
    ],
  },
  {
    id: "team",
    icon: "chat",
    title: "Team and Team Chat",
    sections: [
      {
        h: "Invite staff (owners)",
        steps: [
          "Go to Settings, then open Team.",
          "Tap + Add Staff Member and enter their name and email.",
          "Tap Send Invite. They receive an email to set their password and join your team.",
        ],
        p: ["The usage bar shows how many users your plan allows. Tap Remove next to a member to take away their access, or Refresh Members to update the list."],
      },
      {
        h: "Who sees which customers",
        p: ["Each person sees only the customers they added. Staff cannot see each other's customers."],
      },
      {
        h: "Team Chat",
        p: ["Tap Team Chat to send messages to everyone on your team. Messages are grouped by day. A number on the tile shows unread messages."],
      },
      {
        h: "One device at a time",
        p: ["If you sign in on a second device, the first one is signed out automatically to keep your account secure."],
      },
    ],
  },
  {
    id: "import",
    icon: "download",
    title: "Import and Export",
    sections: [
      {
        p: ["Tap Contacts on the home screen."],
      },
      {
        h: "Import from CSV (Google Contacts)",
        intro: "Import from CSV reads a Google Contacts CSV file. First export your contacts from Google, then import the file here.",
        steps: [
          "On a computer, go to contacts.google.com.",
          "Click Export in the left menu, choose Google CSV, then click Export. The file downloads.",
          "In the app, tap Contacts, then Import from CSV.",
          "Choose the file. You will see a preview of the first 5 contacts.",
          "Tap Import All. A progress bar shows while it works.",
        ],
        list: [
          "Only contacts with a name and a phone number are imported.",
          "Name, phone, email, address, city (saved as Area) and notes are brought in. Everyone starts as a New Lead.",
          "Numbers that are already customers are skipped, so it is safe to import the same file again.",
          "If you exported on a phone, the file is usually in your Downloads folder.",
        ],
      },
      {
        h: "Import from Phone (Android)",
        steps: [
          "Tap Import from Phone.",
          "Filter by source or search by name.",
          "Tick the contacts you want, or tap Select All.",
          "Tap Import.",
        ],
        list: [
          "Numbers that are already customers are skipped automatically, so it is safe to import more than once.",
          "Contacts you have already reviewed are hidden next time. Search by name to find one you skipped.",
          "New phone contacts should sync to your Google account first, which can take a minute.",
        ],
      },
      {
        h: "Export to CSV",
        p: ["Downloads all your customers as a spreadsheet file. On Android, choose where to save or share it."],
      },
      {
        h: "Customers in your phone contacts (Android)",
        p: ["When you sign in, your customers are synced to your phone's contacts, so you see the customer's name when they call."],
      },
    ],
  },
  {
    id: "settings",
    icon: "settings",
    title: "Settings and Account",
    sections: [
      {
        list: [
          "Settings is grouped into Company, Team and billing, App and Account. Tap a row to open it. Only one row is open at a time.",
          "Business Logo: upload a square PNG or JPG, 200 by 200 pixels or larger, up to 5 MB. A transparent background looks best. Owners only.",
          "Tax and Currency, and Measurements (minimum sq ft): owners only. A Save button appears once you change something.",
          "Team: invite and remove staff. Subscription (website only): change plan, card or cancel. Owners only.",
          "Dark mode: the switch changes the app's appearance.",
          "App Info: shows who is signed in, your plan, trial days left and the app version.",
          "Sign out: signs you out of this device.",
          "Delete my account: permanently deletes your company and all its data. Type DELETE to confirm. This cannot be undone.",
        ],
      },
      {
        h: "Automatic sign out",
        p: ["For security, the app signs you out after 8 hours without use."],
      },
    ],
  },
  {
    id: "android",
    icon: "smartphone",
    title: "Android Tips",
    sections: [
      {
        h: "Allow notifications and exact reminders",
        p: ["When the app asks, allow notifications and turn on Alarms and reminders, so reminders arrive at the right time."],
      },
      {
        h: "Stop your phone putting the app to sleep",
        p: ["Some phones, especially Samsung, delay notifications to save battery. On Samsung go to Settings, Battery, Background usage limits, Never sleeping apps, and add BlindsPro CRM."],
      },
      {
        h: "Calls and WhatsApp",
        p: ["Call uses your phone's own dialer and WhatsApp opens the WhatsApp app, so make sure WhatsApp is installed."],
      },
    ],
  },
  {
    id: "plans",
    icon: "card",
    title: "Plans and Billing",
    webOnly: true,
    sections: [
      {
        h: "Free trial",
        p: ["Every new account gets a 15 day free trial with all Pro features. No credit card is needed. We email you 3 days and 1 day before it ends."],
      },
      {
        h: "Plans (prices in CAD)",
        list: [
          "Starter: $19 a month or $190 a year. Up to 200 customers, 1 user (owner), 5 WhatsApp templates.",
          "Pro: $39 a month or $390 a year. Up to 500 customers, up to 5 users including owner, 10 WhatsApp templates, laser measure.",
          "Business: $59 a month or $590 a year. Unlimited customers, up to 10 users including owner, unlimited WhatsApp templates, laser measure.",
        ],
        p: ["Yearly plans save about 17%."],
      },
      {
        h: "How to subscribe",
        p: ["When your trial ends, sign in at blindsprocrm.com and choose a plan. Payment is handled securely by Stripe."],
      },
      {
        h: "Change or cancel",
        p: ["Owners can sign in at blindsprocrm.com, open Settings, Subscription and tap Manage subscription to change plan, update the card or cancel. Or email support@blindsprocrm.com and we will help."],
      },
    ],
  },
  {
    id: "support",
    icon: "mail",
    title: "Contact Support",
    sections: [
      {
        p: ["Email support@blindsprocrm.com and we will reply by email."],
        list: [
          "Your business name.",
          "What you were trying to do and what happened.",
          "A screenshot, if you can.",
          "Your phone model, if it is about the Android app.",
        ],
      },
    ],
  },
];

// PATCH oct21-ui-help: same tips as the i button on the Measurements screen
var TIPS_KEYS = [
  "Tab moves to the next box.",
  "In the fraction list, the arrow keys change the value and stay in the box.",
  "Enter moves to the next box.",
  "Alt + Down arrow opens the fraction list.",
];
var TIPS_PHONE = [
  "After you type a number, it jumps to the fraction box by itself.",
  "The x on a row clears it.",
  "min means the minimum sq ft was charged for that window.",
  "With the laser connected, each shot fills the next box.",
];
function isPhoneDevice() {
  return Capacitor.isNativePlatform() || (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
}

function topicText(t) {
  var parts = [t.title];
  t.sections.forEach(function (s) {
    if (s.h) parts.push(s.h);
    if (s.intro) parts.push(s.intro);
    (s.p || []).forEach(function (x) { parts.push(x); });
    (s.list || []).forEach(function (x) { parts.push(x); });
    (s.steps || []).forEach(function (x) { parts.push(x); });
    if (s.tips) TIPS_KEYS.concat(TIPS_PHONE).forEach(function (x) { parts.push(x); });
  });
  return parts.join(" ").toLowerCase();
}

var H_ICONS = {
  home: ["M3 10.5L12 3l9 7.5", "M5 9.5V21h14V9.5", "M10 21v-6h4v6"],
  key: [{ c: [7.5, 15.5, 4.5] }, "M21 2l-9.6 9.6", "M15.5 7.5l3 3", "M18 5l2 2"],
  users: ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", { c: [9, 7, 4] }, "M23 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"],
  user: ["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2", { c: [12, 7, 4] }],
  link: ["M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71", "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"],
  calendar: ["M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M16 2v4", "M8 2v4", "M3 10h18"],
  clock: [{ c: [12, 12, 9] }, "M12 7v5l3 2"],
  file: ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M8 13h8", "M8 17h5"],
  ruler: ["M3 8h18v8H3z", "M7 8v3", "M11 8v4", "M15 8v3", "M19 8v4"],
  bluetooth: ["M6.5 6.5l11 11L12 23V1l5.5 5.5-11 11"],
  whatsapp: ["M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.1-5.4A8.5 8.5 0 1 1 21 11.5z", "M9 9.5c.5 2.5 2.5 4.5 5 5"],
  map: ["M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z", "M9 4v14", "M15 6v14"],
  chart: ["M3 20h18", "M7 16v-6", "M12 16V5", "M17 16v-4"],
  chat: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
  download: ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M7 10l5 5 5-5", "M12 15V3"],
  settings: ["M4 21v-7", "M4 10V3", "M12 21v-9", "M12 8V3", "M20 21v-5", "M20 12V3", "M1 14h6", "M9 8h6", "M17 16h6"],
  smartphone: ["M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z", "M12 18h.01"],
  card: ["M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z", "M1 10h22"],
  mail: ["M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M22 6l-10 7L2 6"],
  help: [{ c: [12, 12, 9] }, "M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3", "M12 17h.01"],
  arrowLeft: ["M19 12H5", "M12 19l-7-7 7-7"],
  search: [{ c: [11, 11, 7] }, "M21 21l-4.35-4.35"],
  chevronRight: ["M9 18l6-6-6-6"],
  x: ["M18 6L6 18", "M6 6l12 12"],
};
function HIcon({ name, size }) {
  var parts = H_ICONS[name] || H_ICONS.help;
  var s = size || 20;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      {parts.map(function (p, i) { return typeof p === "string" ? <path key={i} d={p} /> : <circle key={i} cx={p.c[0]} cy={p.c[1]} r={p.c[2]} />; })}
    </svg>
  );
}

export default function Help({ theme, C, onClose }) {
  var [query, setQuery] = useState("");
  var [openId, setOpenId] = useState(null);
  var isNative = Capacitor.isNativePlatform();
  var phone = isPhoneDevice();
  var topics = TOPICS.filter(function (t) { return !(t.webOnly && isNative); });
  var q = query.trim().toLowerCase();
  var shown = q ? topics.filter(function (t) { return topicText(t).indexOf(q) !== -1; }) : topics;
  var open = openId ? topics.find(function (t) { return t.id === openId; }) : null;

  var dark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  var tealText = dark ? "#7CC2BB" : C.teal;
  var tealTint = dark ? "#1C2E2C" : "#E6F0EF";
  var wrap = { fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 40, boxSizing: "border-box" };
  var card = { background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 16, marginBottom: 12 };
  var body = { margin: "0 0 8px", color: theme.text, fontSize: 15, lineHeight: 1.6 };
  var btnS = { minHeight: 44, padding: "0 12px", background: theme.sectionBg, color: theme.text, border: "1px solid " + theme.cardBorder, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 600, fontFamily: "inherit" };

  function Header({ title, icon, onBack }) {
    return (
      <div style={{ ...card, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} aria-label="Back" style={btnS}><HIcon name="arrowLeft" size={18} /> Back</button>
        {icon && <span style={{ color: tealText }}><HIcon name={icon} size={22} /></span>}
        <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, lineHeight: 1.2, minWidth: 0 }}>{title}</div>
      </div>
    );
  }

  if (open) {
    return (
      <div style={wrap}>
        {/* PATCH oct21-ui-help */}
        <Header title={open.title} icon={open.icon} onBack={function () { setOpenId(null); window.scrollTo(0, 0); }} />
        {open.sections.filter(function (s) { return !(s.webOnly && isNative); }).map(function (s, i) {
          var list = s.tips ? (phone ? TIPS_PHONE : TIPS_KEYS) : s.list;
          return (
            <div key={i} style={card}>
              {s.h && <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 600, color: theme.text }}>{s.h}</h3>}
              {s.intro && <p style={body}>{s.intro}</p>}
              {s.steps && (
                <ol style={{ ...body, paddingLeft: 22 }}>
                  {s.steps.map(function (x, k) { return <li key={k} style={{ marginBottom: 4 }}>{x}</li>; })}
                </ol>
              )}
              {list && (
                <ul style={{ ...body, paddingLeft: 22 }}>
                  {list.map(function (x, k) { return <li key={k} style={{ marginBottom: 4 }}>{x}</li>; })}
                </ul>
              )}
              {(s.p || []).map(function (x, k) {
                return <p key={k} style={{ ...body, color: s.tips ? theme.mutedText : theme.text, fontSize: s.tips ? 13 : 15 }}>{x}</p>;
              })}
            </div>
          );
        })}
        <button onClick={function () { setOpenId(null); window.scrollTo(0, 0); }}
          style={{ ...btnS, width: "100%", justifyContent: "center", background: theme.card, border: "1px solid " + theme.inputBorder }}>
          <HIcon name="arrowLeft" size={18} /> All help topics
        </button>
      </div>
    );
  }

  return (
    <div style={wrap}>
      {/* PATCH oct21-ui-help */}
      <Header title="Help Center" icon="help" onBack={onClose} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, background: theme.inputBg, border: "1px solid " + theme.inputBorder, borderRadius: 10, padding: "0 6px 0 12px", marginBottom: 12 }}>
        <span style={{ color: theme.mutedText }}><HIcon name="search" size={18} /></span>
        <input
          aria-label="Search help"
          placeholder="Search help, for example reminder or referral"
          value={query}
          onChange={function (e) { setQuery(e.target.value); }}
          style={{ flex: 1, minWidth: 0, minHeight: 42, border: "none", outline: "none", fontSize: 15, background: "transparent", color: theme.text, fontFamily: "inherit" }}
        />
        {query && (
          <button aria-label="Clear search" onClick={function () { setQuery(""); }} style={{ width: 36, height: 36, background: "transparent", border: "none", color: theme.mutedText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HIcon name="x" size={16} />
          </button>
        )}
      </div>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {shown.length === 0 && (
          <div style={{ padding: 16, fontSize: 15, color: theme.subText, textAlign: "center" }}>No topics found. Try another word, or email support@blindsprocrm.com</div>
        )}
        {shown.map(function (t, i) {
          return (
            <button key={t.id} onClick={function () { setOpenId(t.id); window.scrollTo(0, 0); }}
              style={{ width: "100%", minHeight: 56, padding: "8px 14px", background: "transparent", border: "none", borderTop: i ? "1px solid " + theme.cardBorder : "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left", fontFamily: "inherit" }}>
              <span style={{ width: 36, height: 36, borderRadius: 10, background: tealTint, color: tealText, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><HIcon name={t.icon} size={20} /></span>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: theme.text }}>{t.title}</span>
              <span style={{ color: theme.mutedText }}><HIcon name="chevronRight" size={20} /></span>
            </button>
          );
        })}
      </div>
      <div style={{ textAlign: "center", fontSize: 13, color: theme.mutedText, marginTop: 8 }}>
        Still stuck? Email support@blindsprocrm.com<br />BlindsPro CRM by RAKEM Solutions Inc. {/* PATCH oct28-rakem */}
      </div>
    </div>
  );
}
